import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import moment from 'moment-timezone';

import AppRouter from 'components/layouts/AppRouter/AppRouter';
import NavigationPane from 'components/shared/NavigationPane/NavigationPane';
import AppHeader from 'components/shared/AppHeader/AppHeader';
import { AppLoader } from 'components/shared/AppLoader/AppLoader';
import { ConfirmModal } from 'components/shared/ConfirmModal/ConfirmModal';
import { Message } from 'components/shared/Message/Message';
import { ErrorMessage } from 'components/shared/ErrorMessage/ErrorMessage';
import AppErrorBoundary from 'components/shared/AppErrorBoundary/AppErrorBoundary';
import { useTypedDispatch } from 'hooks/useTypedDispatch';
import { isAppLoaderelector, languageSelector, timeZoneSelector } from 'store/app/appSelectors';
import { setIsNavDisabled, setLanguage, setTimeZone } from 'store/app/appReducer';
import { initApp } from 'store/app/appThunk';
import { Token } from 'enum/Token';
import {
  accountSelector, isAuthSelector, serviceSelector,
  userSelector,
} from 'store/auth/authSelectors';
import { LANGUAGE, TIMEZONE } from 'constants/LocalStorage';
import { RoutePath } from 'constants/RoutePath';
import { languages } from 'constants/languages';
import { saveVisitedPage } from 'utils/saveVisitedPage';

import './App.scss';

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useTypedDispatch();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAppLoader = useSelector(isAppLoaderelector);
  const isAuth = useSelector(isAuthSelector);
  const serviceData = useSelector(serviceSelector);
  const accountData = useSelector(accountSelector);
  const user = useSelector(userSelector);
  const timeZone = useSelector(timeZoneSelector);
  const language = useSelector(languageSelector);

  useEffect(() => {
    /*
      by default, momentjs comes with en-us and does not include additional locales for en-us.
      Therefore, when switching the language from something other than en-us back to en-us,
      it can't find the file and perform the translation.
    */
    if (language) {
      const lang = language === languages.EN ? 'en-ca' : language;
      import(`moment/locale/${lang}`)
        .then(() => {
          moment.locale(language);
        }).catch((err) => {
          console.error(err);
        });
    }
  }, [language]);

  useEffect(() => {
    dispatch(setIsNavDisabled(
      pathname === RoutePath.HOME || pathname === RoutePath.PLUG_INS_STORE,
    ));
  }, [pathname]);

  useEffect(() => {
    dispatch(setIsNavDisabled(!serviceData || !accountData));
  }, [serviceData, accountData]);

  useEffect(() => {
    const access_token = searchParams?.get('access_token');
    const refresh_token = searchParams?.get('refresh_token');

    if (access_token && refresh_token) {
      localStorage.setItem(Token.ACCESS_TOKEN, access_token);
      localStorage.setItem(Token.REFRESH_TOKEN, refresh_token);

      setSearchParams(undefined);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(initApp());
  }, []);

  useEffect(() => {
    if (user) {
      const savedTimeZone = localStorage.getItem(`${TIMEZONE}${user.id}`);
      dispatch(setTimeZone(savedTimeZone ?? moment.tz.guess()));

      const savedLanguage = localStorage.getItem(`${LANGUAGE}${user.id}`);
      dispatch(setLanguage(savedLanguage ?? languages.EN));
    }
  }, [user?.id]);

  useEffect(() => {
    if (timeZone && user) {
      localStorage.setItem(`${TIMEZONE}${user.id}`, timeZone);
    }
  }, [timeZone, user?.id]);

  useEffect(() => {
    if (language && user) {
      localStorage.setItem(`${LANGUAGE}${user.id}`, language);
      i18n.changeLanguage(language);
    }
  }, [language, user?.id]);

  useEffect(() => {
    const parts = pathname.split('/');
    const account = parts[1];
    const service = parts[2];
    if (
      user && serviceData && accountData
      && account === accountData.shortName
      && service === serviceData.shortName
    ) {
      saveVisitedPage(window.location.pathname, user.id, accountData.id, serviceData.id);
    }
  }, [pathname, user, serviceData, accountData]);

  if (isAppLoader || !isAuth) {
    return <AppLoader />;
  }

  return (
    <Box className="app" data-testid="app-component">
      <ConfirmModal />
      <NavigationPane />
      <Message />
      <ErrorMessage />
      <Box className="app__main">
        <AppHeader />
        <Box className="content">
          <AppErrorBoundary>
            <AppRouter />
          </AppErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};

export default App;
